/** RFC 0345 S3: project ownership and relay access are signed group grants. */
import { type GroupId, MemberedId } from '@automerge/automerge-repo-keyhive'
import type { CommentsDoc } from '../model/comments/types'
import type { ProjectDoc } from '../model/project/types'
import type { createSharingRepo } from './repo'

export async function createSharingProject(
  sharing: Awaited<ReturnType<typeof createSharingRepo>>,
  person: GroupId,
  options?: { title?: string },
) {
  const { keyhive } = sharing.hive
  const group = await keyhive.generateGroup([person.toIdentifier()])
  await keyhive.revokeMember(
    keyhive.id.toIdentifier(),
    true,
    MemberedId.group(group),
  )
  await sharing.hive.addSyncServerRelayToGroup(group)
  const groupId = Array.from(group.toBytes(), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')
  const project = await sharing.createOwnedDocument<ProjectDoc>(group, {
    title: options?.title ?? 'Project',
    sessionIds: [],
    sessionMeta: {},
    sharingGroupId: groupId,
  })
  // RFC 0345 3g: the owner makes the project's own comments now; a reader
  // could not link them later.
  const comments = await sharing.createOwnedDocument<CommentsDoc>(group, {
    subjectId: project.documentId,
    comments: {},
  })
  project.change((d) => {
    d.commentsId = comments.documentId
  })
  await sharing.repo.flush()
  return { group, project }
}
